import { useState, type FormEvent } from 'react';
import { ApiError, type TenantBranding } from '@ck/sdk';
import { A, api, type StaffTenant } from './api';
import { PageHead } from './ui';

const COLORS: [keyof TenantBranding, string][] = [
  ['primary', 'Brand colour'],
  ['primaryInk', 'Text on brand colour'],
];

export function Branding({ tenant, onChange }: { tenant: StaffTenant; onChange: () => Promise<void> }) {
  const [b, setB] = useState<TenantBranding>(tenant.branding);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      await api.patch(A('/branding'), { ...b, logoUrl: b.logoUrl || null, loginVideoUrl: b.loginVideoUrl || null });
      await onChange();
      setMsg({ ok: true, text: 'Branding saved. The client portal picks it up on next load.' });
    } catch (x) {
      setMsg({ ok: false, text: x instanceof ApiError ? `${x.message}${x.issues ? `: ${x.issues.map((i) => i.path.join('.')).join(', ')}` : ''}` : 'Save failed' });
    } finally { setBusy(false); }
  }

  return (
    <>
    <PageHead kicker="Firm" title="Branding" lead="Your name, logo, accent colour and sign-in video across the client portal, partner portal and this CRM." />
    <div className="grid2">
      <form className="panel" onSubmit={save}>
        <h3>White-label branding</h3>
        <p className="muted">How {tenant.legalName} appears to its traders. Each firm on the platform has its own.</p>
        <div className="grid2 tight">
          <label>Product name<input value={b.name} onChange={(e) => setB({ ...b, name: e.target.value })} /></label>
          <label>Logo mark (1–3 letters)<input maxLength={3} value={b.mark} onChange={(e) => setB({ ...b, mark: e.target.value })} /></label>
          <label className="span2">Logo URL<input type="url" placeholder="https://… (optional)" value={b.logoUrl ?? ''} onChange={(e) => setB({ ...b, logoUrl: e.target.value })} /></label>
          <label className="span2">Support email<input type="email" value={b.supportEmail} onChange={(e) => setB({ ...b, supportEmail: e.target.value })} /></label>
          <label className="span2">Sign-in background video <small>Optional link to an MP4 you own the rights to. Leave empty to use the animated chart.</small><input type="url" placeholder="https://…/login.mp4" value={b.loginVideoUrl ?? ''} onChange={(e) => setB({ ...b, loginVideoUrl: e.target.value })} /></label>
          {COLORS.map(([k, label]) => (
            <label key={k}>{label}
              <span className="color"><input type="color" value={String(b[k])} onChange={(e) => setB({ ...b, [k]: e.target.value.toUpperCase() })} /><input value={String(b[k])} onChange={(e) => setB({ ...b, [k]: e.target.value })} /></span>
            </label>
          ))}
        </div>
        {msg && <p className={msg.ok ? 'ok' : 'err'} role="status">{msg.text}</p>}
        <button className="btn" disabled={busy}>{busy ? 'Saving…' : 'Save branding'}</button>
      </form>
      <section className="panel">
        <h3>Preview</h3>
        <div className="preview" style={{ background: '#F4F4F2', color: '#141414' }}>
          <div className="preview-top">
            {b.logoUrl ? <img src={b.logoUrl} alt="" height={28} /> : <span className="mark" style={{ background: b.primary, color: b.primaryInk }}>{b.mark}</span>}
            <b>{b.name}</b>
          </div>
          <h4>Start a new challenge</h4>
          <p>Pick a plan, choose your size, and trade.</p>
          <span className="preview-btn" style={{ background: b.primary, color: b.primaryInk }}>Start challenge</span>
        </div>
        <p className="muted small">Domains for this firm are set by CK Holdings when the firm is onboarded.</p>
      </section>
    </div>
    </>
  );
}
